import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { CATEGORYLIST } from "../services.js";

/**
 * Event Transformation Utilities
 * Contains pure functions for transforming and filtering calendar events
 */

// Track generated keys for duplicate detection (development only)
const keyRegistry = new Map();
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Logs duplicate key warnings in development mode
 */
const KEY_REGISTRY_MAX = 500;
const logDuplicateKey = (key, event) => {
    if (!isDevelopment) return;
    if (keyRegistry.has(key)) {
        const existingEvent = keyRegistry.get(key);
        console.warn('[eventKey] Duplicate key detected:', {
            key,
            currentEvent: {
                id: event.id || event._id,
                title: event.title || event.titlePlain,
                eventType: event.eventType,
                source: event.extendedProps?.source || event.source,
            },
            existingEvent: {
                id: existingEvent.id || existingEvent._id,
                title: existingEvent.title || existingEvent.titlePlain,
                eventType: existingEvent.eventType,
                source: existingEvent.extendedProps?.source || existingEvent.source,
            },
        });
    }
    if (keyRegistry.size >= KEY_REGISTRY_MAX) {
        keyRegistry.clear();
    }
    keyRegistry.set(key, event);
};

/**
 * Generates a stable unique key for an event
 * Used for tracking events during drag-and-drop operations
 * 
 * @param {object} e - The event object
 * @returns {string} A unique key for the event
 */
export const eventKey = (e) => {
    if (!e) {
        const fallbackKey = `tmp:${uuidv4()}`;
        logDuplicateKey(fallbackKey, e);
        return fallbackKey;
    }

    const src = e?.extendedProps?.source || e?.source;

    // Google events
    if (src === 'google') {
        const googleEventId = e?.extendedProps?.googleEventId;
        if (!googleEventId) {
            // Fallback: use id, start date, and UUID for uniqueness
            const fallbackKey = `g:fallback:${e.id || e._id || 'unknown'}:${e.start ? moment(e.start).format('YYYY-MM-DD-HHmm') : ''}:${uuidv4()}`;
            logDuplicateKey(fallbackKey, e);
            return fallbackKey;
        }
        const key = `g:${googleEventId}`;
        logDuplicateKey(key, e);
        return key;
    }

    // Microsoft events
    if (src === 'microsoft') {
        const base = e?.extendedProps?.microsoftEventId;
        if (!base) {
            // Fallback: use id, start date, and UUID for uniqueness
            const fallbackKey = `m:fallback:${e.id || e._id || 'unknown'}:${e.start ? moment(e.start).format('YYYY-MM-DD-HHmm') : ''}:${uuidv4()}`;
            logDuplicateKey(fallbackKey, e);
            return fallbackKey;
        }
        const isSeries = e?.extendedProps?.recurrence || e?.extendedProps?.recurringEventId || false;
        const occ = isSeries && e.start ? `:${moment(e.start).format('YYYY-MM-DD')}` : '';
        const key = `m:${base}${occ}`;
        logDuplicateKey(key, e);
        return key;
    }

    // Local events
    if (src === 'local') {
        const localId = e._id || e?.extendedProps?.localEventId;
        if (!localId) {
            // Fallback: use id and UUID for uniqueness
            const fallbackKey = `l:fallback:${e.id || 'unknown'}:${uuidv4()}`;
            logDuplicateKey(fallbackKey, e);
            return fallbackKey;
        }
        const key = `l:${localId}`;
        logDuplicateKey(key, e);
        return key;
    }

    // Holiday events
    if (e?.eventType === 'Holiday') {
        const holidayId = e.holidayId || e.id || e._id;
        if (!holidayId) {
            // Fallback: use date and UUID for uniqueness
            const dateStr = e.start ? moment(e.start).format('YYYY-MM-DD') : e.date || 'unknown';
            const fallbackKey = `h:fallback:${dateStr}:${e.titlePlain || e.title || 'unknown'}:${uuidv4()}`;
            logDuplicateKey(fallbackKey, e);
            return fallbackKey;
        }
        const key = `h:${holidayId}`;
        logDuplicateKey(key, e);
        return key;
    }

    // Leave events
    if (e?.eventType === 'Leave') {
        const leaveId = e.leaveId || e.id || e._id;
        if (!leaveId) {
            // Fallback: use date range and UUID for uniqueness
            const startStr = e.start ? moment(e.start).format('YYYY-MM-DD') : e.startDate || 'unknown';
            const endStr = e.end ? moment(e.end).format('YYYY-MM-DD') : e.endDate || startStr;
            const fallbackKey = `leave:fallback:${startStr}:${endStr}:${uuidv4()}`;
            logDuplicateKey(fallbackKey, e);
            return fallbackKey;
        }
        const key = `leave:${leaveId}`;
        logDuplicateKey(key, e);
        return key;
    }

    // Birthday events
    if (e?.eventType === 'Birthday') {
        const titlePlain = e.titlePlain || e.title || 'unknown';
        const dateStr = e.start ? moment(e.start).format('YYYY-MM-DD') : e.date || 'unknown';
        // Add user ID if available for better uniqueness
        const userId = e.userId || e.user?._id || '';
        const key = `b:${titlePlain}:${dateStr}${userId ? `:${userId}` : ''}`;
        logDuplicateKey(key, e);
        return key;
    }

    // Anniversary events
    if (e?.eventType === 'Anniversary') {
        const titlePlain = e.titlePlain || e.title || 'unknown';
        const dateStr = e.start ? moment(e.start).format('YYYY-MM-DD') : e.date || 'unknown';
        // Add user ID if available for better uniqueness
        const userId = e.userId || e.user?._id || '';
        const key = `a:${titlePlain}:${dateStr}${userId ? `:${userId}` : ''}`;
        logDuplicateKey(key, e);
        return key;
    }

    // Fallback for unknown event types
    const fallbackKey = e.id || e._id || `tmp:${uuidv4()}`;
    logDuplicateKey(fallbackKey, e);
    return fallbackKey;
};

/**
 * Applies optimistic updates to events
 * Used during drag-and-drop operations to provide immediate visual feedback
 */
export const applyOptimistic = (base, patches) =>
    base.map((ev) => {
        const k = eventKey(ev);
        const p = patches[k];
        if (!p) return ev;

        // minimally patch time fields; keep everything else intact
        const patched = {
            ...ev,
            start: p.start,
            end: p.end,
            allDay: p.allDay,
            extendedProps: {
                ...ev.extendedProps,
                startDate: moment(p.start).format('YYYY-MM-DD'),
                endDate: moment(p.end || p.start).format('YYYY-MM-DD'),
                startTime: p.allDay ? null : moment(p.start).format('HH:mm'),
                endTime: p.allDay ? null : moment(p.end).format('HH:mm'),
            },
        };

        return patched;
    });

/**
 * Filters events based on search term and selected categories
 */
export const filterEvents = (events, filter, selectedCategories) => {
    const list = Array.isArray(events) ? events : [];
    const lower = (filter || '').toLowerCase();
    // Ensure selectedCategories is always an array
    const categories = Array.isArray(selectedCategories) ? selectedCategories : [];

    return list.filter((event) => {
        const titleStr = String(event?.titlePlain ?? event?.title ?? "");
        const matchesTitle = lower ? titleStr.toLowerCase().includes(lower) : true;

        const matchesCategory = categories.length > 0
            ? categories.some((category) => {
                const categoryConfig = CATEGORYLIST.find((c) => c.name === category);
                return categoryConfig?.eventTypes?.includes(event?.eventType);
            })
            : true;

        return matchesTitle && matchesCategory;
    });
};

